import React from 'react';
import type { HorseEmergency } from '../../../../types/horseProfile';

interface EmergencyEditFormProps {
  initialData: HorseEmergency;
  onSubmit: (data: HorseEmergency) => Promise<void>;
}

const DEFAULT_EMERGENCY_DATA: HorseEmergency = {
  primaryVet: {
    name: '',
    phone: '',
    email: ''
  },
  backupVet: {
    name: '',
    phone: '',
    email: ''
  },
  insurance: {
    provider: '',
    policyNumber: '',
    expiryDate: '',
    coverage: ''
  },
  specialNeeds: [],
  allergies: []
};

export const EmergencyEditForm: React.FC<EmergencyEditFormProps> = ({
  initialData = DEFAULT_EMERGENCY_DATA,
  onSubmit
}) => {
  const [formData, setFormData] = React.useState<HorseEmergency>({
    ...DEFAULT_EMERGENCY_DATA,
    ...initialData
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Haupttierarzt</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Name
            </label>
            <input
              type="text"
              value={formData.primaryVet.name}
              onChange={(e) => setFormData({
                ...formData,
                primaryVet: { ...formData.primaryVet, name: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Telefon
            </label>
            <input
              type="tel"
              value={formData.primaryVet.phone}
              onChange={(e) => setFormData({
                ...formData,
                primaryVet: { ...formData.primaryVet, phone: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              E-Mail
            </label>
            <input
              type="email"
              value={formData.primaryVet.email}
              onChange={(e) => setFormData({
                ...formData,
                primaryVet: { ...formData.primaryVet, email: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Vertretungstierarzt</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Name
            </label>
            <input
              type="text"
              value={formData.backupVet.name}
              onChange={(e) => setFormData({
                ...formData,
                backupVet: { ...formData.backupVet, name: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Telefon
            </label>
            <input
              type="tel"
              value={formData.backupVet.phone}
              onChange={(e) => setFormData({
                ...formData,
                backupVet: { ...formData.backupVet, phone: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              E-Mail
            </label>
            <input
              type="email"
              value={formData.backupVet.email}
              onChange={(e) => setFormData({
                ...formData,
                backupVet: { ...formData.backupVet, email: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Versicherung</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Versicherer
            </label>
            <input
              type="text"
              value={formData.insurance.provider}
              onChange={(e) => setFormData({
                ...formData,
                insurance: { ...formData.insurance, provider: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Policen-Nr.
            </label>
            <input
              type="text"
              value={formData.insurance.policyNumber}
              onChange={(e) => setFormData({
                ...formData,
                insurance: { ...formData.insurance, policyNumber: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Gültig bis
            </label>
            <input
              type="date"
              value={formData.insurance.expiryDate}
              onChange={(e) => setFormData({
                ...formData,
                insurance: { ...formData.insurance, expiryDate: e.target.value }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Leistungsumfang
            </label>
            <textarea
              value={formData.insurance.coverage}
              onChange={(e) => setFormData({
                ...formData,
                insurance: { ...formData.insurance, coverage: e.target.value }
              })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Besondere Bedürfnisse
        </label>
        <input
          type="text"
          value={formData.specialNeeds.join(', ')}
          onChange={(e) => setFormData({
            ...formData,
            specialNeeds: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
          })}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          placeholder="Mit Komma getrennt eingeben"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Allergien
        </label>
        <input
          type="text"
          value={formData.allergies.join(', ')}
          onChange={(e) => setFormData({
            ...formData,
            allergies: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
          })}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          placeholder="Mit Komma getrennt eingeben"
        />
      </div>
    </form>
  );
};